import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useModalFocus } from '../hooks/useModalFocus.js';
import { parseLatLon } from '../utils/parseLatLon.js';

const API = import.meta.env.VITE_API_BASE || '/api';

export default function LocationDialog({ observer, onSave, onClose }) {
  const [query, setQuery] = useState('');
  const [coordinates, setCoordinates] = useState(observer ? `${observer.lat.toFixed(4)}, ${observer.lon.toFixed(4)}` : '');
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const dialog = useRef(null);
  const generation = useRef(0);
  const controller = useRef(null);
  useModalFocus(dialog, onClose);
  useEffect(() => () => { generation.current++; controller.current?.abort(); }, []);
  function save(location) {
    onSave(location);
    onClose();
  }
  function gps() {
    setError('');
    if (!navigator.geolocation) { setError('GPS is unavailable. Search for a city or postcode, or enter coordinates.'); return; }
    const token = ++generation.current;
    setBusy('gps');
    navigator.geolocation.getCurrentPosition(pos => {
      if (token !== generation.current) return;
      save({ lat: pos.coords.latitude, lon: pos.coords.longitude, name: 'GPS location' });
    }, err => {
      if (token !== generation.current) return;
      setBusy('');
      setError(err.code === 1 ? 'Location permission denied. Search for a city or postcode, or enter coordinates.' : 'GPS could not find your location. Try again, search for a city or postcode, or enter coordinates.');
    }, { timeout: 10000, maximumAge: 60000 });
  }
  async function search(e) {
    e.preventDefault();
    setError(''); setResults([]);
    const q = query.trim();
    if (q.length < 2) { setError('Enter at least two characters, such as a city and country or a postcode.'); return; }
    const token = ++generation.current;
    controller.current?.abort();
    const abort = new AbortController(); controller.current = abort;
    const timeout = setTimeout(() => abort.abort(), 12000);
    setBusy('search');
    try {
      const response = await fetch(`${API}/locations/search?q=${encodeURIComponent(q)}`, { signal: abort.signal });
      if (!response.ok) throw new Error('lookup unavailable');
      const data = await response.json();
      if (token !== generation.current) return;
      setResults(data.locations || []);
      if (!data.locations?.length) setError('No places found. Add a region or country, try a postcode, or enter coordinates.');
    } catch {
      if (token === generation.current) setError('Place search is unavailable. Try again shortly, use GPS, or enter coordinates.');
    } finally { clearTimeout(timeout); if (token === generation.current) setBusy(''); }
  }
  function keyDown(e) {
    if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
    if (e.key !== 'Tab') return;
    const items = [...dialog.current.querySelectorAll('button:not(:disabled),input:not(:disabled),a[href]')].filter(n => n.getClientRects().length);
    const first = items[0], last = items.at(-1);
    if (e.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) { e.preventDefault(); last?.focus(); }
    else if (!e.shiftKey && (document.activeElement === last || document.activeElement === dialog.current)) { e.preventDefault(); first?.focus(); }
  }
  return createPortal(
    <div className="og-location-backdrop" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <section className="og-location-dialog" role="dialog" aria-modal="true" aria-labelledby="location-title" tabIndex={-1} ref={dialog} onKeyDown={keyDown}>
        <div className="og-location-title"><h2 id="location-title">{observer ? 'Change location' : 'Set your location'}</h2><button onClick={onClose} aria-label="Close location setup">Close</button></div>
        <p>Choose where you observe to personalize Tonight, sky conditions and passes.</p>
        {observer && <p className="og-saved-location">Saved: {observer.name || 'Coordinates'} · {observer.lat.toFixed(4)}°, {observer.lon.toFixed(4)}°</p>}
        <button className="og-primary" onClick={gps} disabled={!!busy}>{busy === 'gps' ? 'Finding your location…' : 'Use GPS'}</button>
        <form onSubmit={search}>
          <label htmlFor="location-place">City or postcode</label>
          <div className="og-location-input-row"><input id="location-place" value={query} maxLength={120} onChange={e => setQuery(e.target.value)} placeholder="Springfield, TN or 37172" autoComplete="off" /><button disabled={!!busy}>{busy === 'search' ? 'Searching…' : 'Find'}</button></div>
          <span className="og-location-hint">Search runs when you select Find. Choose a result to save it.</span>
        </form>
        {error && <p role="alert" className="og-location-error">{error}</p>}
        {results.length > 0 && <ul className="og-location-results" aria-label="Matching places">{results.map((place, i) => <li key={`${place.lat}:${place.lon}:${i}`}><button onClick={() => save(place)}>{place.name}<small>{place.lat.toFixed(4)}°, {place.lon.toFixed(4)}°</small></button></li>)}</ul>}
        <form onSubmit={e => { e.preventDefault(); const location = parseLatLon(coordinates); if (!location) { setError('Enter valid latitude, longitude: latitude −90 to 90; longitude −180 to 180.'); return; } save({ ...location, name: 'Coordinates' }); }}>
          <label htmlFor="location-coordinates">Or enter latitude, longitude</label>
          <div className="og-location-input-row"><input id="location-coordinates" value={coordinates} onChange={e => setCoordinates(e.target.value)} placeholder="36.5092, −86.8850" /><button disabled={!!busy}>Save</button></div>
        </form>
        <p className="og-location-hint">Place search: © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap contributors</a>, via Nominatim. City/postcode searches are sent to this service. Your saved observing location stays in this browser.</p>
      </section>
    </div>, document.body
  );
}
